const moment = require("moment-timezone");
const roleDisplayTexts = require("../assets/json/userRoles.json");

const sleep = (time) => new Promise((resolve, reject) => setTimeout(resolve, time));

const mergeText = (text, replacement) => text.replace(/\|(.*)\|/, replacement);

const mergeTags = (text, tags) => {
  let mergedText = text;
  tags.forEach(({ tag, replacement }) => {
    mergedText = mergedText.replace(`|${tag}|`, replacement);
  });
  return mergedText;
};

const datasetsHaveData = (datasets) => {
  if (!datasets || !datasets.length) return false;
  return datasets.some((dataset) => dataset.data.some((data) => data !== 0));
};

const filterBetweenDates = (value, filter) => {
  if (filter[0] === "" && filter[1] === "") return true;
  if (filter[0] === "") return moment(value) <= moment(filter[1]).endOf("days");
  if (filter[1] === "") return moment(value) >= moment(filter[0]).startOf("days");
  return (
    moment(value) >= moment(filter[0]).startOf("days") &&
    moment(value) <= moment(filter[1]).endOf("days")
  );
};

const formatDate = (date, format = "DD/MM/YYYY") => {
  return moment(date).format(format);
};

const getDuration = (
  startDate,
  finishDate,
  dateInputFormat = "DD/MM/YYYY",
  timeUnit = "M",
  roundMethod = Math.round
) => {
  const startMoment = moment(startDate, dateInputFormat);
  const finishMoment = moment(finishDate, dateInputFormat);
  const duration = moment.duration(finishMoment.diff(startMoment));
  if (roundMethod) return roundMethod(duration.as(timeUnit));
  return duration.as(timeUnit);
};

const momentFromUnix = (unixTimestamp) => {
  return moment.unix(unixTimestamp / 1000);
};

const cleanString = (string) => string.trim().toLowerCase();

const findString = (searched, filter) => {
  const foundIndex = searched.search(new RegExp(filter));
  if (foundIndex === -1) return false;
  return true;
};

const getCleanFullName = (firstName, lastName) =>
  cleanString(firstName) + " " + cleanString(lastName);

const filterFullName = (value, filter) => {
  if (!filter) return true;
  if (!value) return false;
  return findString(
    getCleanFullName(value.name.first, value.name.last),
    cleanString(filter)
  );
};

const states = {
  pending: "pending",
  active: "active",
  finished: "finished",
  future: "future",
};

const checkPrograms = (program) => {
  let _program = {};
  if (!program) _program = { ...program };
  else _program = { ...program };

  const startAt =
    moment(_program?.startAt).startOf("day") ||
    moment(_program?.createdAt).startOf("day");
  const expiredAt = moment(_program?.expiredAt).endOf("day");
  const now = moment();

  if (moment(startAt).startOf("day").isBefore(now)) return states.pending;
  if (moment(startAt).startOf("day").isBetween(now, expiredAt)) return states.active;
  if (moment(startAt).startOf("day").isAfter(now)) return states.future;
  if (moment(expiredAt).isAfter(now)) return states.finished;
  throw TypeError("STATUS_NOT_FOUND");
};

const getActiveProgram = (programs) => {
  if (programs.length === 1) return { ...programs[0] };
  const activePrograms = programs.filter((p) => {
    const state = checkPrograms(p);
    if (states.active === state) return { ...p, state };
  });

  const sortPrograms = activePrograms.sort((a, b) => a.startAt - b.startAt);
  return { ...sortPrograms[0] };
};

const formatChartLabels = (labels) => {
  if (!labels || !labels[0]) return [];
  const currentLabel = labels[0].split(" ")[0];
  const nextLabel = labels.length >= 14 ? "Sem" : "Semana";
  const shouldChangeLabel = currentLabel !== nextLabel;
  if (!shouldChangeLabel) return labels;
  return labels.map((label) => label.replace(currentLabel, nextLabel));
};

const formatCLPCurrency = (number) => {
  // Check if the input is a valid number
  if (typeof number !== "number" || isNaN(number)) {
    throw new Error("Invalid input. Please provide a valid number.");
  }

  // Create a NumberFormat object with the desired options
  const formatter = new Intl.NumberFormat("es-CL", {
    style: "currency",
    currency: "CLP",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

  // Format the number and return the result
  return formatter.format(number);
};

const getRoleDisplayText = (userType, userRole) => {
  return roleDisplayTexts[userType].find((role) => role.value === userRole).text;
};

function mergeObjects(obj1, obj2) {
  // Create a deep copy of the first object to preserve its structure
  const result = structuredClone(obj1);

  // Helper function to recursively merge objects
  function recursiveMerge(target, source) {
    for (const key in source) {
      if (source.hasOwnProperty(key)) {
        if (source[key] !== null && source[key] !== undefined) {
          // If the property in the source object is not null or undefined,
          // update the corresponding property in the target object
          if (source[key] instanceof Object && key in target) {
            // If the current property is an object, recurse into it
            recursiveMerge(target[key], source[key]);
          } else {
            // Otherwise, update the property in the target object
            target[key] = source[key];
          }
        }
      }
    }
  }

  // Start the recursive merging process
  recursiveMerge(result, obj2);

  return result;
}

/**
 * Calculate the percentage of a numeric amount relative to a calculus base.
 *
 * @param {number} amount - The numeric amount.
 * @param {number} base - The calculus base.
 * @returns {number} The calculated percentage
 * @throws {Error} If either amount or base is not a number, or base is zero.
 * @example
 * // Returns 50
 * calculatePercentage(50, 100);
 * @example
 * // Throws an error: "Invalid input. Please provide valid numeric values for amount and base, and ensure base is not zero."
 * calculatePercentage('abc', 0);
 */
const calculatePercentage = (amount, base) => {
  if (+base === 0) {
    return 0; // Avoid division by zero
  }

  if (isNaN(+amount) || isNaN(+base)) {
    return 0; // Return 0 if either amount or base is not a number
  }
  return (+amount / +base) * 100;
};

const UNKNOWN_BROWSER = "unknown";

// Long enough for any real version string ("126.0.6478.153" is 14 characters) and
// short enough to fit the `activities.browserVersion` column downstream.
const MAX_VERSION_LENGTH = 32;

// Ordered most-specific first: every iOS browser and every Chromium fork carries
// the `Safari/` and/or `Chrome/` suffix, so the generic entries must stay last.
// Group 1 holds the version for every entry except Safari, which resolves it
// through its own `versionRegex`. Un-namespaced Chromium forks (Brave, Vivaldi,
// Yandex) intentionally roll up into the generic Chrome bucket — a new fork only
// gets its own name by adding a row ABOVE that one.
const BROWSER_PATTERNS = [
  { name: "Mozilla Firefox (iOS)", regex: /FxiOS\/([\d.]+)/ },
  { name: "Mozilla Firefox", regex: /Firefox\/([\d.]+)/ },
  { name: "Samsung Internet", regex: /SamsungBrowser\/([\d.]+)/ },
  { name: "Microsoft Edge (iOS)", regex: /EdgiOS\/([\d.]+)/ },
  { name: "Microsoft Edge (Chromium)", regex: /Edg(?:A|W)?\/([\d.]+)/ },
  { name: "Microsoft Edge (Legacy)", regex: /Edge\/([\d.]+)/ },
  { name: "Opera (iOS)", regex: /(?:OPiOS|OPT)\/([\d.]+)/ },
  { name: "Opera", regex: /(?:Opera|OPR)\/([\d.]+)/ },
  { name: "Google Chrome (iOS)", regex: /CriOS\/([\d.]+)/ },
  { name: "Google Chrome or Chromium", regex: /Chrome\/([\d.]+)/ },
  {
    name: "Apple Safari",
    regex: /Safari\/[\d.]+/,
    versionRegex: /Version\/([\d.]+)/,
  },
];

/**
 * Resolves the browser name and version from a user agent string.
 * Never throws: any unrecognized, empty or non-string input resolves to "unknown".
 *
 * @param {string} [userAgent=navigator.userAgent] - user agent to inspect
 * @returns {{ browserName: string, browserVersion: string }}
 *
 * @example
 * getBrowserInfo("Mozilla/5.0 ... Chrome/126.0.0.0 Safari/537.36");
 * // { browserName: "Google Chrome or Chromium", browserVersion: "126.0.0.0" }
 *
 * @example
 * // Safari reports its version in a separate `Version/` segment that in-app
 * // webviews omit, so the version falls back to "unknown".
 * getBrowserInfo("Mozilla/5.0 (iPhone; ...) Mobile/15E148 Safari/604.1");
 * // { browserName: "Apple Safari", browserVersion: "unknown" }
 */
const getBrowserInfo = (
  userAgent = typeof navigator === "undefined" ? "" : navigator.userAgent
) => {
  const ua = typeof userAgent === "string" ? userAgent : "";

  for (const { name, regex, versionRegex } of BROWSER_PATTERNS) {
    const match = ua.match(regex);
    if (!match) continue;

    const version = (versionRegex ? ua.match(versionRegex) : match)?.[1];

    return {
      browserName: name,
      // The user agent is client-controlled and the version pattern is unbounded,
      // so the value is capped to fit the column that ultimately stores it.
      browserVersion: version?.slice(0, MAX_VERSION_LENGTH) ?? UNKNOWN_BROWSER,
    };
  }

  return { browserName: UNKNOWN_BROWSER, browserVersion: UNKNOWN_BROWSER };
};

const boolEnv = (env) => /true/i.test(env);

export function debounce(fn, delay) {
  var timeoutID = null;
  return function () {
    clearTimeout(timeoutID);
    var args = arguments;
    var that = this;
    timeoutID = setTimeout(function () {
      fn.apply(that, args);
    }, delay);
  };
}

const deepDeleteKey = (obj, key) => {
  if (Array.isArray(obj)) {
    obj.forEach((item) => deepDeleteKey(item, key));
  } else if (obj !== null && typeof obj === "object") {
    delete obj[key];
    Object.values(obj).forEach((value) => deepDeleteKey(value, key));
  }
  return obj;
};

export {
  boolEnv,
  calculatePercentage,
  cleanString,
  datasetsHaveData,
  filterBetweenDates,
  filterFullName,
  findString,
  formatChartLabels,
  formatCLPCurrency,
  formatDate,
  getActiveProgram,
  getBrowserInfo,
  getCleanFullName,
  getDuration,
  getRoleDisplayText,
  mergeObjects,
  mergeTags,
  mergeText,
  momentFromUnix,
  sleep,
  deepDeleteKey,
};
